import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";

export type Language = "ar" | "en";
export type Direction = "rtl" | "ltr";

interface LanguageContextType {
  language: Language;
  direction: Direction;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

interface LanguageProviderProps {
  children: ReactNode;
}

// Translations
const translations = {
  ar: {
    // Navigation
    "portal.title": "بوابة WSM الداخلية",
    "nav.home": "الرئيسية",
    "nav.me": "عنّي",
    "nav.requests": "طلباتي",
    "nav.org_structure": "الهيكل التنظيمي",
    "nav.news": "الأخبار",
    "nav.events": "الفعاليات",
    "nav.notifications": "الإشعارات",
    "nav.calendar": "التقويم",
    "nav.documents": "المستندات",
    "nav.settings": "الإعدادات",
    "nav.attendance": "سجل الحضور",
    "nav.my_tasks": "مهامي",
    "nav.my_info": "معلوماتي",
    "nav.outlook": "البريد",
    "nav.teams": "تيمز",
    "nav.wasmi": "وسمي",
    "common.coming_soon": "قريبًا",
    "announcement.dismiss": "حسنًا",
    "announcement.wasmi.title": "تعرّف على وسمي",
    "announcement.wasmi.description": "مساعد ذكاء اصطناعي خاص بالشركة، مثل ChatGPT وClaude، يمكنك تسجيل الدخول إليه واستخدامه في عملك اليومي.",
    "announcement.wasmi.feature_chat": "تحدث واطرح أسئلتك بالعربية أو الإنجليزية",
    "announcement.wasmi.feature_requests": "أنشئ طلباتك وتابعها بسهولة",
    "announcement.wasmi.feature_ideas": "احصل على مساعدة في الكتابة والأفكار والتلخيص",

    // Common
    "common.search": "البحث...",
    "common.new": "جديد",
    "common.profile": "الملف الشخصي",
    "common.logout": "تسجيل الخروج",
    "common.save": "حفظ",
    "common.cancel": "إلغاء",
    "common.edit": "تعديل",
    "common.delete": "حذف",
    "common.view": "عرض",
    "common.loading": "جاري التحميل...",
    "common.no_data": "لا توجد بيانات بعد",

    // Profile
    "profile.overview": "نظرة عامة",
    "profile.tasks": "مهامي",
    "profile.requests": "طلباتي",
    "profile.attendance": "الحضور",
    "profile.files": "الملفات",
    "profile.update": "تحديث الملف الشخصي",
    "profile.password": "كلمة المرور",
    "profile.upcoming_events": "الاجتماعات القادمة",
    "profile.latest_news": "آخر الأخبار",
    "profile.request_status": "حالة الطلبات",

    // Requests
    "requests.new": "طلب جديد",
    "requests.status": "الحالة",
    "requests.type": "النوع",
    "requests.date": "التاريخ",
    "requests.pending": "قيد الانتظار",
    "requests.approved": "موافق عليه",
    "requests.rejected": "مرفوض",
    "requests.draft": "مسودة",
    "requests.in_progress": "قيد التنفيذ",
    "requests.completed": "مكتمل",
    "requests.vacation": "إجازة",
    "requests.asset": "طلب أصول/IT",
    "requests.business_card": "بطاقة عمل",
    "requests.hr_letter": "خطاب HR",

    // News
    "news.read_more": "اقرأ المزيد",
    "news.category": "الفئة",
    "news.share": "مشاركة",
    "news.related": "أخبار ذات صلة",

    // Events
    "events.rsvp": "تأكيد الحضور",
    "events.interested": "مهتم",
    "events.going": "سأحضر",
    "events.venue": "المكان",
    "events.organizer": "المنظم",
    "events.attachments": "المرفقات",

    // Notifications
    "notifications.mark_read": "وضع علامة كمقروء",
    "notifications.mark_all_read": "وضع علامة على الكل كمقروء",
    "notifications.unread": "غير مقروء",

    // Services
    "nav.services": "الخدمات",
    "services.title": "الخدمات",
    "services.description": "جميع الخدمات والطلبات المتاحة للموظفين",
    "services.start_request": "بدء الطلب",
    // services-salary-certificate
    "services-salary-certificate.title":"طلب تعريف راتب",
    "services-remote-work.title":"طلب عمل عن بعد ",
    "services-leave-permission.title":"طلب إذن مغادرة ",
    "services-leave-application.title":"طلب إجازة",
    "services-technical-support.title":"طلب دعم فني",

    Present: "حاضر",
    Holiday: "إجازة",

    "tasks.overdue": "متأخر",
    "tasks.Open": "Open",
    "tasks.Start": "بدء",
    "tasks.Finished": "منتهي",
    "tasks.Rejected": "مرفوض",
    "tasks.Closed": "مغلق",
    "tasks.Working": "جاري العمل عليه",

    // Attendance & Missing Hours
    "attendance.missing_hours_summary": "ملخص الساعات المفقودة",
    "attendance.total_required_hours": "إجمالي الساعات المطلوبة",
    "attendance.total_worked_hours": "إجمالي الساعات المنجزة",
    "attendance.total_missing_hours": "إجمالي الساعات المفقودة",
    "attendance.days_with_missing_hours": "أيام بها ساعات مفقودة",
    "attendance.days_with_missing_hours_checks":
      "أيام بها ساعات أو بصمات مفقودة",
    "attendance.days_with_missing_checks": "أيام بها بصمات مفقودة",
    "attendance.monthly_progress": "تقدم العمل الشهري",
    "attendance.monthly": "شهرياً",
    "attendance.completion_rate": "إنجاز",
    "attendance.shortage_rate": "نقص",
    "attendance.hours_per_day": "ساعة/يوم",
    "attendance.day": "يوم",
    "attendance.request_hours_adjustment": "طلب تعديل ساعات",
    "attendance.view_detailed_reports": "عرض التقارير التفصيلية",
    "attendance.handle_missing_hours": "معالجة الساعات المفقودة",
    "attendance.hours": "ساعة",
    "attendance.refresh": "تحديث",
    "attendance.great_job": "أحسنت! 🎉",
    "attendance.all_hours_completed":
      "لقد أكملت جميع الساعات المطلوبة لهذا الشهر",

    // Me Page
    "me.title": "عنّي",
    "me.subtitle": "معلوماتي الشخصية والمهنية",
    "me.export_profile": "تصدير الملف الشخصي",
    "me.share": "مشاركة",
    "me.edit": "تعديل",
    "me.cancel_edit": "إلغاء التعديل",
    "me.performance_rating": "تقييم الأداء",
    "me.quick_stats": "إحصائيات سريعة",
    "me.teams": "فريق العمل",
    "me.years_of_service": "سنوات الخدمة",
    "me.age": "العمر",
    "me.join_date": "تاريخ الانضمام",
    "me.direct_manager": "المدير المباشر",
    "me.overview": "نظرة عامة",
    "me.professional": "معلومات مهنية",
    "me.skills": "المهارات",
    "me.performance": "الأداء",
    "me.personal": "معلومات شخصية",
    "me.basic_info": "المعلومات الأساسية",
    "me.work_info": "معلومات العمل",
    "me.salary_info": "معلومات الراتب",
    "me.employee_id": "رقم الموظف",
    "me.first_name": "الاسم الأول",
    "me.last_name": "اسم العائلة",
    "me.birth_date": "تاريخ الميلاد",
    "me.position": "المنصب",
    "me.department": "القسم",
    "me.manager": "المدير",
    "me.location": "الموقع",
    "me.base_salary": "الراتب الأساسي",
    "me.benefits": "بدلات",
    "me.total_salary": "إجمالي الراتب",
    "me.click_eye_to_view": "انقر على العين لعرض معلومات الراتب",
    "me.education": "التعليم",
    "me.certifications": "الشهادات",
    "me.education_certifications": "التعليم و الشهادات",
    "me.expires": "ينتهي",
    "me.technical_skills": "المهارات التقنية",
    "me.languages": "اللغات",
    "me.achievements": "الإنجازات",
    "me.phone": "رقم الهاتف",
    "me.emergency_contact": "جهة اتصال الطوارئ",
    "me.relationship": "العلاقة",
    "me.preferences": "التفضيلات",
    "me.notifications": "الإشعارات",
    "me.profile_privacy": "خصوصية الملف الشخصي",
    "me.appearance": "المظهر",
    "me.enabled": "مفعلة",
    "me.disabled": "معطلة",
    "me.years": "سنوات",
    "me.year": "سنة",

    // Documents
    "documents.title": "مستنداتي",
    "documents.description": "الوصول إلى مستنداتك وإدارتها من أرشيف الشركة",
    "documents.upload": "رفع",
    "documents.new_folder": "مجلد جديد",
    "documents.search_placeholder": "البحث في المستندات...",
    "documents.all_categories": "جميع الفئات",
    "documents.all_departments": "جميع الأقسام",
    "documents.all_status": "جميع الحالات",
    "documents.grid_view": "عرض شبكي",
    "documents.list_view": "عرض قائمة",
    "documents.sort_by": "ترتيب حسب",
    "documents.select_all": "تحديد الكل",
    "documents.deselect_all": "إلغاء تحديد الكل",
    "documents.no_documents": "لم يتم العثور على مستندات",
    "documents.no_documents_description": "حاول تعديل معايير البحث أو المرشحات",
    "documents.upload_first": "رفع أول مستند لك",
    "documents.selected": "محدد",
    "documents.download_all": "تحميل الكل",
    "documents.share": "مشاركة",
    "documents.add_to_favorites": "إضافة إلى المفضلة",
    "documents.delete": "حذف",

    // my_info
    "my_info.title": "معلوماتي",
    "my_info.personal_details": "التفاصيل الشخصية",
    "my_info.contact_information": "معلومات الاتصال",
    "my_info.emergency_contacts": "جهات اتصال الطوارئ",
    "my_info.job_information": "معلومات الوظيفة",

    // Dashboard
    "dashboard.pending_requests": "الطلبات المعلقة",
    "dashboard.completed_requests": "الطلبات المكتملة",
    "dashboard.monthly_events": "الفعاليات هذا الشهر",
    "dashboard.new_notifications": "الإشعارات الجديدة",
    "dashboard.attendance_record": "سجل الحضور",
    "dashboard.view_full_attendance": "عرض سجل الحضور الكامل",
    "dashboard.activity_overview": "إليك نظرة عامة على أنشطتك اليوم",
    "dashboard.news.pilot_launch": "الإطلاق التجريبي لبوابة وسم الداخلية",
    "dashboard.news.national_day": "غداء اليوم الوطني السعودي",
    "dashboard.news.tech_category": "تقنية",
    "dashboard.news.general_category": "عام",

    // Requests
    "requests.no_leave_requests": "لا توجد طلبات إجازات",
    "requests.no_permission_requests": "لا توجد طلبات أذونات",
    "requests.no_work_outside_requests": "لا توجد طلبات عمل خارج المكتب",
    "requests.from": "من:",
    "requests.to": "إلى:",
    "requests.duration": "المدة:",
    "requests.days": "أيام",
    "requests.hours": "ساعات",
    "requests.total_duration": "المدة الإجمالية:",

    // Development Alert
    "dev.alert.title": "تنبيه تطوير",
    "dev.alert.message":
      "⚠️ هذه الصفحة قيد التطوير من قبل إدارة المنتجات، بعض المعلومات هي لمجرد العرض",

    // Index Page
    "index.welcome": "مرحباً بك في تطبيقك",
    "index.start_building": "ابدأ بناء مشروعك الرائع هنا!",

    // Common additional
    "common.from": "من",
    "common.to": "إلى",
    "common.duration": "المدة",
    "common.total": "الإجمالي",
    "common.not_calculated": "غير محسوب",
    "common.not_specified": "غير محدد",
    "common.no_data_found": "لا توجد بيانات",
    "common.date": "التاريخ",

    // Attendance specific
    "attendance.in_time": "وقت الدخول",
    "attendance.out_time": "وقت الخروج",
    "attendance.total_time": "إجمالي الوقت",
    "attendance.no_checkout": "لم يسجل الخروج",
    "attendance.no_records": "لا توجد سجلات حضور",
    "attendance.completed_8_hours": "إكمال 8 ساعات؟",
    "attendance.yes": "نعم",
    "attendance.no": "لا",

    // Status translations
    "status.present": "حاضر",
    "status.absent": "غائب",
    "status.holiday": "عطلة",
    "status.on_leave": "إجازة",

    // Tasks
    "tasks.start_date": "بداية:",
    "tasks.end_date": "نهاية:",
    "tasks.no_tasks": "لا توجد مهام",
    "tasks.upcoming_tasks": "ستظهر مهامك المقبلة هنا",
    "tasks.view_all": "عرض جميع المهام",

    // Requests specific
    "requests.title": "الطلبات",
    "requests.leave_requests": "طلبات الإجازات",
    "requests.permission_requests": "طلبات الأذونات",
    "requests.work_outside_requests": "طلبات العمل خارج المكتب",
    "requests.permission_request": "طلب إذن",
    "requests.work_outside_request": "طلب عمل خارج المكتب",
    "requests.technical_support_requests":"طلبات الدعم الفني",
    "requests.technical_support_request":"طلب دعم فني",
    "requests.date_label": "التاريخ:",
    "requests.duration_label": "المدة:",
    "requests.track_requests": "يمكنك متابعة طلباتك من مكان واحد",
  },
  en: {
    // Navigation
    "portal.title": "WSM Internal Portal",
    "nav.home": "Home",
    "nav.me": "Me",
    "nav.requests": "Requests",
    "nav.org_structure": "Org Structure",
    "nav.news": "News",
    "nav.events": "Events",
    "nav.notifications": "Notifications",
    "nav.calendar": "Calendar",
    "nav.documents": "Documents",
    "nav.settings": "Settings",
    "nav.attendance": "Attendance",
    "nav.my_tasks": "My Tasks",
    "nav.my_info": "My Info",
    "nav.outlook": "Outlook",
    "nav.teams": "Teams",
    "nav.wasmi": "Wasmi",
    "common.coming_soon": "Coming soon",
    "announcement.dismiss": "Got it",
    "announcement.wasmi.title": "Meet Wasmi",
    "announcement.wasmi.description": "The company's own AI assistant, like ChatGPT and Claude. Sign in and use it in your daily work.",
    "announcement.wasmi.feature_chat": "Chat and ask questions in Arabic or English",
    "announcement.wasmi.feature_requests": "Create and track your requests easily",
    "announcement.wasmi.feature_ideas": "Get help with writing, ideas and summaries",

    // Common
    "common.search": "Search...",
    "common.new": "New",
    "common.profile": "Profile",
    "common.logout": "Logout",
    "common.save": "Save",
    "common.cancel": "Cancel",
    "common.edit": "Edit",
    "common.delete": "Delete",
    "common.view": "View",
    "common.loading": "Loading...",
    "common.no_data": "No data yet",

    // Profile
    "profile.overview": "Overview",
    "profile.tasks": "My Tasks",
    "profile.requests": "My Requests",
    "profile.attendance": "Attendance",
    "profile.files": "Files",
    "profile.update": "Update Profile",
    "profile.password": "Password",
    "profile.upcoming_events": "Upcoming Events",
    "profile.latest_news": "Latest News",
    "profile.request_status": "Request Status",

    // Requests
    "requests.new": "New Request",
    "requests.status": "Status",
    "requests.type": "Type",
    "requests.date": "Date",
    "requests.pending": "Pending",
    "requests.approved": "Approved",
    "requests.rejected": "Rejected",
    "requests.draft": "Draft",
    "requests.in_progress": "In Progress",
    "requests.completed": "Completed",
    "requests.vacation": "Vacation",
    "requests.asset": "Asset/IT Request",
    "requests.business_card": "Business Card",
    "requests.hr_letter": "HR Letter",

    // News
    "news.read_more": "Read More",
    "news.category": "Category",
    "news.share": "Share",
    "news.related": "Related News",

    // Events
    "events.rsvp": "RSVP",
    "events.interested": "Interested",
    "events.going": "Going",
    "events.venue": "Venue",
    "events.organizer": "Organizer",
    "events.attachments": "Attachments",

    // Notifications
    "notifications.mark_read": "Mark as Read",
    "notifications.mark_all_read": "Mark All as Read",
    "notifications.unread": "Unread",

    // Services
    "nav.services": "Services",
    "services.title": "Services",
    "services.description": "All available services and requests for employees",
    "services.start_request": "Start Request",
    // services-salary-certificate
    "services-salary-certificate.title":"Salary Certificate Request",
    "services-remote-work.title":"Remote Work Request",
    "services-leave-permission.title":"Leave Permission Request",
    "services-leave-application.title":"Leave Application",
    "services-technical-support.title":"Technical Support Request",

    // Attendance & Missing Hours
    "attendance.missing_hours_summary": "Missing Hours Summary",
    "attendance.total_required_hours": "Total Required Hours",
    "attendance.total_worked_hours": "Total Worked Hours",
    "attendance.total_missing_hours": "Total Missing Hours",
    "attendance.days_with_missing_hours": "Days with Missing Hours",
    "attendance.days_with_missing_hours_checks":
      "Days with Missing Hours or Checks",
    "attendance.days_with_missing_checks": "Days with Missing Checks",
    "attendance.monthly_progress": "Monthly Work Progress",
    "attendance.monthly": "Monthly",
    "attendance.completion_rate": "Completion",
    "attendance.shortage_rate": "Shortage",
    "attendance.hours_per_day": "hours/day",
    "attendance.day": "day",
    "attendance.request_hours_adjustment": "Request Hours Adjustment",
    "attendance.view_detailed_reports": "View Detailed Reports",
    "attendance.handle_missing_hours": "Handle Missing Hours",
    "attendance.hours": "hours",
    "attendance.refresh": "Refresh",
    "attendance.great_job": "Great Job! 🎉",
    "attendance.all_hours_completed":
      "You have completed all required hours for this month",

    // Me Page
    "me.title": "Me",
    "me.subtitle": "My Personal and Professional Information",
    "me.export_profile": "Export Profile",
    "me.share": "Share",
    "me.edit": "Edit",
    "me.cancel_edit": "Cancel Edit",
    "me.performance_rating": "Performance Rating",
    "me.quick_stats": "Quick Stats",
    "me.teams": "Teams",
    "me.years_of_service": "Years of Service",
    "me.age": "Age",
    "me.join_date": "Join Date",
    "me.direct_manager": "Direct Manager",
    "me.overview": "Overview",
    "me.professional": "Professional",
    "me.skills": "Skills",
    "me.performance": "Performance",
    "me.personal": "Personal",
    "me.basic_info": "Basic Information",
    "me.work_info": "Work Information",
    "me.salary_info": "Salary Information",
    "me.employee_id": "Employee ID",
    "me.first_name": "First Name",
    "me.last_name": "Last Name",
    "me.birth_date": "Date of Birth",
    "me.position": "Position",
    "me.department": "Department",
    "me.manager": "Manager",
    "me.location": "Location",
    "me.base_salary": "Base Salary",
    "me.benefits": "Benefits",
    "me.total_salary": "Total Salary",
    "me.click_eye_to_view": "Click the eye to view salary information",
    "me.education": "Education",
    "me.certifications": "Certifications",
    "me.education_certifications": "Education & Certifications",
    "me.expires": "Expires",
    "me.technical_skills": "Technical Skills",
    "me.languages": "Languages",
    "me.achievements": "Achievements",
    "me.phone": "Phone Number",
    "me.emergency_contact": "Emergency Contact",
    "me.relationship": "Relationship",
    "me.preferences": "Preferences",
    "me.notifications": "Notifications",
    "me.profile_privacy": "Profile Privacy",
    "me.appearance": "Appearance",
    "me.enabled": "Enabled",
    "me.disabled": "Disabled",
    "me.years": "years",
    "me.year": "year",

    // Documents
    "documents.title": "My Documents",
    "documents.description":
      "Access and manage your documents from the company archive",
    "documents.upload": "Upload",
    "documents.new_folder": "New Folder",
    "documents.search_placeholder": "Search documents...",
    "documents.all_categories": "All Categories",
    "documents.all_departments": "All Departments",
    "documents.all_status": "All Status",
    "documents.grid_view": "Grid View",
    "documents.list_view": "List View",
    "documents.sort_by": "Sort By",
    "documents.select_all": "Select All",
    "documents.deselect_all": "Deselect All",
    "documents.no_documents": "No documents found",
    "documents.no_documents_description":
      "Try adjusting your search criteria or filters",
    "documents.upload_first": "Upload your first document",
    "documents.selected": "selected",
    "documents.download_all": "Download All",
    "documents.share": "Share",
    "documents.add_to_favorites": "Add to Favorites",
    "documents.delete": "Delete",

    // my_info
    "my_info.title": "My Info",
    "my_info.personal_details": "Personal Details",
    "my_info.contact_information": "Contact Information",
    "my_info.emergency_contacts": "Emergency Contacts",
    "my_info.job_information": "Job Information",

    // Dashboard
    "dashboard.pending_requests": "Pending Requests",
    "dashboard.completed_requests": "Completed Requests",
    "dashboard.monthly_events": "Monthly Events",
    "dashboard.new_notifications": "New Notifications",
    "dashboard.attendance_record": "Attendance Record",
    "dashboard.view_full_attendance": "View Full Attendance Record",
    "dashboard.activity_overview":
      "Here's an overview of your activities today",
    "dashboard.news.pilot_launch": "WSM Internal Portal Pilot Launch",
    "dashboard.news.national_day": "Saudi National Day Lunch",
    "dashboard.news.tech_category": "Technology",
    "dashboard.news.general_category": "General",

    // Requests
    "requests.no_leave_requests": "No leave requests found",
    "requests.no_permission_requests": "No permission requests found",
    "requests.no_work_outside_requests":
      "No work outside office requests found",
    "requests.from": "From:",
    "requests.to": "To:",
    "requests.duration": "Duration:",
    "requests.days": "days",
    "requests.hours": "hours",
    "requests.total_duration": "Total Duration:",

    // Development Alert
    "dev.alert.title": "Development Alert",
    "dev.alert.message":
      "⚠️ This page is still under development by the Product Department - Some features may be unavailable or in progress",

    // Index Page
    "index.welcome": "Welcome to Your Blank App",
    "index.start_building": "Start building your amazing project here!",

    // Common additional
    "common.from": "From",
    "common.to": "To",
    "common.duration": "Duration",
    "common.total": "Total",
    "common.not_calculated": "Not calculated",
    "common.not_specified": "Not specified",
    "common.no_data_found": "No data found",
    "common.date": "Date",

    // Attendance specific
    "attendance.in_time": "In Time",
    "attendance.out_time": "Out Time",
    "attendance.total_time": "Total Time",
    "attendance.no_checkout": "No record",
    "attendance.no_records": "No attendance records",
    "attendance.completed_8_hours": "Completed 8 hours?",
    "attendance.yes": "Yes",
    "attendance.no": "No",

    // Status translations
    "status.present": "Present",
    "status.absent": "Absent",
    "status.holiday": "Holiday",
    "status.on_leave": "On Leave",

    // Tasks
    "tasks.start_date": "Start:",
    "tasks.end_date": "End:",
    "tasks.no_tasks": "No tasks",
    "tasks.upcoming_tasks": "Your upcoming tasks will appear here",
    "tasks.view_all": "View All Tasks",

    // Requests specific
    "requests.title": "Requests",
    "requests.leave_requests": "Leave Requests",
    "requests.permission_requests": "Permission Requests",
    "requests.work_outside_requests": "Work Outside Office Requests",
    "requests.permission_request": "Permission Request",
    "requests.work_outside_request": "Work Outside Office Request",
    "requests.technical_support_requests":"Technical Support Requests",
    "requests.technical_support_request":"Technical Support Request",
    "requests.date_label": "Date:",
    "requests.duration_label": "Duration:",
    "requests.track_requests": "You can track all your requests in one place",
  },
};

export const LanguageProvider: React.FC<LanguageProviderProps> = ({
  children,
}) => {
  // Initialize language from localStorage or default to "en" if no preference is stored
  const getInitialLanguage = (): Language => {
    const savedLanguage = localStorage.getItem("wsm-portal-language");
    return (savedLanguage as Language) || "en";
  };

  const [language, setLanguage] = useState<Language>(getInitialLanguage);
  const direction: Direction = language === "ar" ? "rtl" : "ltr";

  useEffect(() => {
    // Update document direction and lang
    document.documentElement.dir = direction;
    document.documentElement.lang = language;
  }, [language, direction]);

  const handleLanguageChange = (lang: Language) => {
    setLanguage(lang);
    // Save to localStorage for persistence
    localStorage.setItem("wsm-portal-language", lang);
  };

  const t = (key: string): string => {
    return (
      translations[language][
        key as keyof (typeof translations)[typeof language]
      ] || key
    );
  };

  const value: LanguageContextType = {
    language,
    direction,
    setLanguage: handleLanguageChange,
    t,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
